import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DatePicker, Select } from 'syngenta-digital-cropwise-react-ui-kit';
import { useHistory, useLocation } from 'react-router-dom';

import ChevronDownIcon from 'assets/chevron_down.svg';
import ProductDetailInvoiceTable from '../ProductDetailInvoiceTable';
import ProductDetailTasksTable from '../ProductDetailTasksTable';
import ProductDetailCropSeasonSelect from '../ProductDetailCropSeasonSelect';
import { ProductDetailsContext } from '../../context/ProductDetailsProvider';
import './index.css';

const { RangePicker } = DatePicker;

export const PRODUCT_DETAIL_TABLES = {
  invoices: ProductDetailInvoiceTable,
  applications: ProductDetailTasksTable,
  ready_tasks: () => <h1>Ready Tasks</h1>
};

const selectOptions = [
  { key: 'invoices', value: 'Invoices' },
  { key: 'applications', value: 'Applications' }
];

const toRangeValue = dateRange =>
  dateRange?.length
    ? [moment(dateRange[0]), dateRange[1] ? moment(dateRange[1]) : null]
    : null;

// The tables compare invoice and application dates against local-midnight
// `Date`s, which is what grow-ui's flatpickr range emitted.
const toDateRange = dates =>
  dates?.every(Boolean)
    ? dates.map(date =>
        date
          .clone()
          .startOf('day')
          .toDate()
      )
    : null;

const ProductDetailsTableHeader = ({
  cropSeasons,
  onChangeCropSeasons,
  onChangeDateRange,
  dateRange = null
}) => {
  const history = useHistory();
  const location = useLocation();
  const { isUpdateListPrice } = useContext(ProductDetailsContext);

  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location]);

  const selectValue = useMemo(() => {
    const option = selectOptions.find(
      ({ key }) => key === queryParams.get('table')
    );
    return option?.value ?? 'Invoices';
  }, [queryParams]);

  const onChangeViewMode = useCallback(
    (_, option) => {
      const params = new URLSearchParams({ table: option.key });
      history.replace({
        pathname: location.pathname,
        search: params.toString()
      });
    },
    [history, location]
  );

  return (
    <section className="flex justify-between mb-4">
      <div className="flex items-center gap-4">
        <h2 className="text-xl font-semibold ml-4 truncate">
          Production Records
        </h2>
        <div className="w-52">
          <Select
            style={{ width: 180 }}
            name="table"
            value={selectValue}
            disabled={isUpdateListPrice}
            onChange={onChangeViewMode}
            suffixIcon={
              <img
                width={18}
                height={18}
                alt="chevron down"
                src={ChevronDownIcon}
              />
            }
          >
            {selectOptions.map(({ key, value }) => (
              <Select.Option key={key} value={value}>
                {value}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-82">
          <ProductDetailCropSeasonSelect
            onChange={onChangeCropSeasons}
            values={cropSeasons}
            selectValue={selectValue}
          />
        </div>
        <div
          className="product-detail-date-range"
          data-testid="product-detail-date-range"
        >
          <RangePicker
            value={toRangeValue(dateRange)}
            format="MM/DD/YYYY"
            onChange={dates => onChangeDateRange(toDateRange(dates))}
          />
        </div>
      </div>
    </section>
  );
};

ProductDetailsTableHeader.propTypes = {
  cropSeasons: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChangeCropSeasons: PropTypes.func.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.any),
  onChangeDateRange: PropTypes.func.isRequired
};

export default ProductDetailsTableHeader;
